import React, { useImperativeHandle, useRef, useState } from "react";
import { formatCurrency } from "@/utils/formatters";
import { Heading } from "@/components/ui/Heading";
import { RadioGroup, Radio } from "@/components/ui/Radio";
import { GiftAidProps } from "./GiftAidProps";
import classes from "./GiftAid.module.scss";
import { useDonationFormStore } from "@/stores";
import { clsx } from "clsx";
import { getAsset } from "@/utils/assets";
import { useTranslation } from "@/hooks/useTranslation";
import { Checkbox } from "../ui/Checkbox";
import { FormControl } from "../ui/FormControl";
import { focusAndScrollIntoView } from "@/utils/dom";

const EXTRA_AMOUNT = 0.25;

export function GiftAid(props: GiftAidProps) {
  const { config, className, ref } = props;
  const { isUKTaxPayer, setUKTaxPayer, donationAmount } =
    useDonationFormStore();

  const amount = donationAmount ?? 0;
  const donationFormated = formatCurrency(amount);
  const donationWithExtra = formatCurrency(amount + amount * EXTRA_AMOUNT);
  const [error, setError] = useState<string | null>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  const t = useTranslation("gift_aid");
  const postText = t(
    config["gift-aid-post"].i18n,
    { amount: donationWithExtra },
    {
      // A cleared rich-text editor can still contain empty paragraphs and breaks.
      transform: (html) =>
        html.replace(/<[^<>]*>|&nbsp;/g, "").trim() ? html : "",
    },
  );
  const hasPostText = Boolean(postText) && React.Children.count(postText) > 0;

  const value =
    typeof isUKTaxPayer === "boolean" ? (isUKTaxPayer ? "yes" : "no") : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setUKTaxPayer(e.target.value === "yes");
  };

  const hasRadioButtons = config["gift-aid-allow-multiple-selections"];
  const showImpact = amount > 0 && !!config["gift-aid-impact-card"]?.enabled;

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!hasRadioButtons) return { isValid: true };

      const isValid = typeof isUKTaxPayer === "boolean";
      if (!isValid) {
        setError("Please confirm if we can claim Gift Aid on your donations");
        focusAndScrollIntoView(firstRadioRef.current);
      } else {
        setError(null);
      }

      return {
        isValid,
      };
    },
  }));

  return (
    <div className={clsx(classes.ukTaxpayer, className)}>
      <div
        className={clsx(
          classes.card,
          showImpact && classes.withIllustration,
          error && classes.hasError,
        )}
        style={
          {
            "--giftaid-illustration": `url(${getAsset("/images/giftaid-illustration.png")})`,
          } as React.CSSProperties
        }
      >
        <div className={classes.header}>
          <Heading as="h3" size="lg" gutterBottom>
            {t(config["gift-aid-title"].i18n)}
          </Heading>

          <img
            className={classes.logo}
            src={getAsset("/images/giftaid-logo.svg")}
            width={140}
            height={49}
            alt=""
          />
        </div>

        {showImpact && (
          <>
            <div className={clsx(classes.impactLead, classes.resetParagraphs)}>
              {t("impact-lead-html")}
            </div>

            <div className={classes.impactAmounts}>
              <div className={classes.impactItem}>
                <span className={classes.impactValue}>{donationFormated}</span>
                <span className={classes.impactLabel}>
                  {t("your-donation")}
                </span>
              </div>

              <svg
                className={classes.impactArrow}
                width="76"
                height="109"
                viewBox="0 0 76 109"
                fill="currentColor"
                aria-hidden="true"
              >
                {/* Shaft snapped to x=18 so it lines up with the bars below. */}
                <path d="M76 50L38 0L0 50H18V73H58V50H76Z" />
                <rect x="18" y="77" width="40" height="14" />
                <rect x="18" y="95" width="40" height="14" />
              </svg>

              <div className={clsx(classes.impactItem, classes.giftAid)}>
                <span className={classes.impactValue}>{donationWithExtra}</span>
                <span className={classes.impactLabel}>
                  {t("your-donation-with-gift-aid")}
                </span>
              </div>
            </div>
          </>
        )}

        {showImpact && <hr className={classes.divider} />}

        <div className={classes.intro}>
          {t(config["gift-aid-intro"].i18n, {
            amount: donationFormated,
            gift_aid_amount: donationWithExtra,
          })}
        </div>

        <div className={classes.formGroup}>
          {hasRadioButtons && (
            <FormControl
              type="fieldset"
              label={t("select-option")}
              error={error}
              required
            >
              <RadioGroup
                name="uk-taxpayer"
                value={value}
                direction="column"
                className={classes.radioGroup}
                onChange={handleChange}
              >
                <Radio variant="check" value="yes" ref={firstRadioRef}>
                  {t(config["gift-aid-option-yes"].i18n)}
                </Radio>

                <Radio variant="times" value="no">
                  {t(config["gift-aid-option-no"]?.i18n ?? "")}
                </Radio>
              </RadioGroup>
            </FormControl>
          )}

          {!hasRadioButtons && (
            <Checkbox
              checked={isUKTaxPayer ?? false}
              onChange={(ev) => setUKTaxPayer(ev.target.checked)}
            >
              {t(config["gift-aid-option-yes"].i18n)}
            </Checkbox>
          )}
        </div>

        {hasPostText && (
          <div className={clsx(classes.postText, classes.resetParagraphs)}>
            {postText}
          </div>
        )}
      </div>
    </div>
  );
}
